/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Jinhyoung Jang (jangjh@rsupport.com)
 *
 * @description
 *
 * @created at 2024-10-17 16:26:38
 **/
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */

import produce from "immer";
import omit from "lodash-es/omit";
import { usePersistStore } from "@/frameworks/core/persistence";
import { ChatService, SpaceService } from "@/frameworks/core/API";
import { InitialSubscribeTopics, Topics } from "@/frameworks/core/commons";
import type { ChatDispatch } from "./states";

const LOGTAG = "#Chats:Actions#";

const CHAT_TYPES = [
	"roomCreated",
	"roomDeleted",
	"roomNameUpdated",
	"roomNoticeNotified",
	"roomViewed",
	"messageSent",
	"messageUpdated",
	"messageDeleted",
	"messageFileDeleted",
	"messageRead",
	"messageReaded",
	"memberAdded",
	"memberRoomPinned",
	"memberRoomHided",
	"memberLeft",
	"memberNotificationUpdated",
	"roomOwnerChanged"
];

/**
 * The Actions class implements the Dispatch interface and provides methods to load necessary data for
 * various chat functionalities such as topics, chat list, and available rooms.
 */
export default class ChatActions implements ChatDispatch {
	private static _instance: ChatActions | null = null;
	private constructor() {}
	public static get instance() {
		if (!ChatActions._instance) {
			ChatActions._instance = new ChatActions();
		}
		return ChatActions._instance;
	}

	// variables
	private _chatAPI = new ChatService({});

	/**
	 * Handles the event when a message is received.
	 *
	 * @param {Message.Frame<Message.StompMessageTypes, any, Message.Options>} message - The message frame received.
	 * @param {string} topic - The topic under which the message was received.
	 *
	 * @return {Promise<void>} - A promise that resolves after processing the message.
	 */
	public async on_message_received(
		message: Message.Frame<Message.StompMessageTypes, any, Message.Options>,
		topic: string
	): Promise<void> {
		const { type, payload } = message;
		if (!CHAT_TYPES.includes(type)) {
			return;
		}

		switch (type) {
			case "roomCreated": {
				console.debug(LOGTAG, "on_message_received", type, payload, topic);
				break;
			}

			default: {
				// console.debug(LOGTAG, "on_message_received", message, topic);
				break;
			}
		}
	}

	/**
	 * Loads the necessary data for topics, chat list, and available rooms, then
	 * executes a callback with the generated topic list.
	 *
	 * @param {function} callback - A function that will be executed after data
	 * has been loaded. The function takes a single argument, which is an array of topics.
	 *
	 * @return {Promise<void>} A promise that resolves when the data is loaded and
	 * the callback function has been executed.
	 */
	public async load(callback: (topics: string[]) => void): Promise<void> {
		this.make_rooms().then(() => {
			callback(this.make_topiclist());
			console.debug(LOGTAG, "Ready to rooms info");
		});
	}

	// private

	/**
	 * Generates and updates the room list in the persistent store. It checks if the room list is empty,
	 * and if so, fetches lists of different room types (dm, space, group, openworld). The method then
	 * updates the persistent store with the fetched room lists. Afterward, it verifies each room type
	 * list individually and updates the state if they are empty.
	 *
	 * @return {Promise<void>} A promise that resolves when the room list has been successfully generated and updated.
	 * @private
	 */
	private async make_rooms(): Promise<void> {
		const lists = await Promise.all([
			this.get_dm_list(),
			this.get_space_list(),
			this.get_group_list(),
			this.get_openworld_list()
		]);

		usePersistStore.setState((state) =>
			produce(state, (draft) => {
				draft.rooms["dm"] = lists[0];
				draft.rooms["space"] = lists[1];
				draft.rooms["group"] = lists[2];
				draft.rooms["openworld"] = lists[3];
			})
		);
	}

	/**
	 * Constructs a list of topics to which users should be subscribed based on the available room data
	 * fetched from the persisted state store.
	 *
	 * @return {string[]} An array of topic strings representing chat rooms the user is subscribed to.
	 *
	 * @private
	 */
	private make_topiclist(): string[] {
		const { rooms } = usePersistStore.getState();
		let result: string[] = InitialSubscribeTopics();
		if (rooms.dm) {
			result = result.concat(Object.keys(rooms.dm).map((key) => Topics.Room(key)));
		}
		if (rooms.space) {
			result = result.concat(
				Object.values(rooms.space).reduce<string[]>((total, current) => {
					const { rooms } = current as WebServices.SpaceChat.List.Item;
					const room_topics = rooms.filter((room) => room.roomJoined).map((room) => Topics.Room(room.roomId));
					return total.concat(room_topics);
				}, [])
			);
		}
		//console.debug(LOGTAG, "chat topics:", result);
		return result;
	}

	/**
	 * Updates the state with the provided list for the specified room type.
	 *
	 * @param {"dm" | "space" | "group" | "openworld"} key - The type of room to update.
	 * @param {any} list - The new room list to set.
	 * @return {void}
	 * @private
	 */
	private set_rooms_state(key: "dm" | "space" | "group" | "openworld", list: any): void {
		usePersistStore.setState((state) =>
			produce(state, (draft) => {
				draft.rooms[key] = list;
			})
		);
	}

	/**
	 * Fetches the direct message list via the chat API and returns the data formatted.
	 *
	 * @return {Promise<{ [k: string]: WebServices.DirectMessage.List.Data[number] }>} A promise that resolves to an object in which
	 *                                                                                 keys are room IDs and values are direct message data.
	 * @private
	 */
	private async get_dm_list(): Promise<{ [k: string]: WebServices.DirectMessage.List.Data[number] }> {
		const { data } = await this._chatAPI.chatDM.list.execute();
		const result: Map<string, WebServices.DirectMessage.List.Data[number]> = new Map();
		data?.forEach((item) => {
			result.set(item.roomId, item);
		});
		return Object.fromEntries(result);
	}

	/**
	 * Retrieves a list of spaces from the chat API and returns them as an object.
	 *
	 * @return {Promise<Object<string, WebServices.SpaceChat.List.Data[number]>>} An object where the keys are space IDs and the values are the corresponding chat space data.
	 * @private
	 */
	private async get_space_list(): Promise<{ [k: string]: WebServices.SpaceChat.List.Data[number] }> {
		const { data } = await this._chatAPI.chatSpace.list.execute();
		const result: Map<string, WebServices.SpaceChat.List.Data[number]> = new Map();
		data?.forEach((item) => {
			item.rooms.forEach((room) => {
				if (room.spaceId) {
					result.set(room.spaceId, item);
				}
			});
		});
		return Object.fromEntries(result);
	}

	/**
	 * Retrieves and processes the list of grouped chat data.
	 *
	 * This method fetches the grouped chat list from the chat API, processes the data,
	 * and formats it into an object of grouped space chats.
	 *
	 * @return {Promise<{ [k: string]: WebServices.SpaceChat.GroupedList.Data }>} A promise that resolves to an object where the key is the spaceId and
	 * the value is the corresponding grouped chat data.
	 * @private
	 */
	private async get_group_list(): Promise<{ [k: string]: WebServices.SpaceChat.GroupedList.Data }> {
		const { data } = await this._chatAPI.chatSpace.groupedList.execute();
		const result: Map<string, WebServices.SpaceChat.GroupedList.Data> = new Map();
		if (data && data.myRoomChat) {
			result.set(data.myRoomChat.spaceId, {
				myRoomChat: data.myRoomChat,
				groupedSpaceChats: data.groupedSpaceChats
			});
		}
		return Object.fromEntries(result);
	}

	/**
	 * Fetches and returns a list of open world information from SpaceService.
	 *
	 * @return {Promise<{ [k: string]: WebServices.Space.InqueryPublicOpenWorldInfoList.Data[number] }>}
	 *         A promise that resolves to an object where each key is a space ID and the value is the corresponding
	 *         open world information data.
	 * @private
	 */
	private async get_openworld_list(): Promise<{
		[k: string]: WebServices.Space.InqueryPublicOpenWorldInfoList.Data[number];
	}> {
		const spaceAPI = new SpaceService({});
		const { data } = await spaceAPI.spacePublicOpenWorld.inquiryOpenworldList.execute();
		const result: Map<string, WebServices.Space.InqueryPublicOpenWorldInfoList.Data[number]> = new Map();
		data?.forEach((item) => {
			result.set(item.spaceId, item);
		});
		return Object.fromEntries(result);
	}

	public chat_draft(roomID: string): ChatServices.Format2.Container.Richtext | null {
		const { draft } = usePersistStore.getState();
		return draft[roomID] || null;
	}

	public save_chat_draft(roomID: string, text: ChatServices.Format2.Container.Richtext | null) {
		usePersistStore.setState((state) =>
			produce(state, (draft) => {
				if (text) {
					draft.draft[roomID] = text;
				} else {
					draft.draft = omit(draft.draft, roomID);
				}
			})
		);
	}

	public check_has_draft(roomID: string, show: boolean): void {
		const { draft } = usePersistStore.getState();
		const content = draft[roomID];

		const isEmptyDraft =
			!content ||
			content.elements.length === 0 ||
			content.elements.every((el) => el.type === "text" && el.text.trim() === "");

		usePersistStore.setState((state) =>
			produce(state, (draft) => {
				draft.hasDraft[roomID] = show && !isEmptyDraft;
			})
		);
	}

	public has_draft(roomID: string): boolean {
		const { draft } = usePersistStore.getState();
		const content = draft[roomID];

		if (!content || content.elements.length === 0) {
			return false;
		}

		const isEmptyDraft = content.elements.every((el) => el.type === "text" && el.text.trim() === "");

		return !isEmptyDraft;
	}
}
